import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Caractéristique } from '../models/caractéristique.model';
import { CaracteristiqueCreateRequest } from '../requests/caractéristique-create-request';
import { CaractéristiqueStore } from '../store/caractéristique.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-sous-caracteristiques',
  imports: [
    ButtonAddNewComponent,
    HlmCardImports,
    ReactiveFormsModule,
    HlmFieldImports,
    HlmInputImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-sous-caracteristiques.component.html',
})
export class FormSousCaracteristiquesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #caractéristiqueStore = inject(CaractéristiqueStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  readonly form = this.#fb.group({
    sousCaracteristiques: this.#fb.array([]),
  });

  readonly caracteristiqueParente = input.required<Caractéristique>({ alias: 'caracteristique' });
  readonly referentiel = input.required<Référentiel>();

  ngOnInit(): void {
    this.addSousCaracteristique();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addSousCaracteristique() {
    const sousCaractéristique = this.#fb.group({
      nom: [null, [Validators.required]],
    });
    this.sousCaracteristiques.push(sousCaractéristique);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouvellesSousCaractéristiques: CaracteristiqueCreateRequest = {
      idReferentiel: this.referentiel().id,
      idCaracteristiqueParente: this.caracteristiqueParente().id,
      caracteristiques: (this.sousCaracteristiques.value as any[])
        .filter((x: { nom: string }) => !!x.nom?.trim())
        .map((x) => ({ nom: x.nom, estSecteur: false })),
    };

    if (
      !nouvellesSousCaractéristiques.caracteristiques ||
      nouvellesSousCaractéristiques.caracteristiques.length === 0
    ) {
      this.form.markAllAsTouched();
      return;
    }

    this.#caractéristiqueStore.créer({
      data: nouvellesSousCaractéristiques,
      next: () => {
        this.#caractéristiqueStore.charger(this.referentiel().id);
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des sous-caractéristiques réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouvellesSousCaractéristiques.caracteristiques),
    });
  }

  alreadyExists(
    nouvellesSousCaractéristiques: {
      nom: string;
    }[],
  ) {
    const sousCaractéristiquesDuParent = this.caracteristiqueParente().sousCaracteristiques.map((x) => x.nom);

    const doublonsAvecCaractéristiquesExistantes = sousCaractéristiquesDuParent.filter((x) =>
      nouvellesSousCaractéristiques.map((y) => y.nom).includes(x),
    );

    this.sousCaracteristiques.controls.forEach((control) => {
      if (
        doublonsAvecCaractéristiquesExistantes.includes(control.value.nom) ||
        this.sousCaracteristiques.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });
  }

  get sousCaracteristiques() {
    return this.form.get('sousCaracteristiques') as FormArray;
  }
}
